import moment from 'moment-timezone';
import Moment = moment.Moment;
import { PayableApprovalRule } from './payable-approval-rules';
import { PayableApproval } from './payable-approvals';
import { PayabliPayment } from './payabli-payment';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { PrintableCheck } from './printable-check';
import { BillPayment } from './bill-payment';
import { Transaction } from './transactions/transaction';
import { UnifiedBankAccount } from './unified-bank-account';

// Generic interface for fulfillment references that aligns with FulfillmentTypeInterface
export interface FulfillmentReference {
    // Common properties that should be available across all fulfillment types
    id?: number;
    paymentAmount?: number;
    scheduledPaymentDate?: Moment;
    paymentSentAt?: Moment;
    expectedDeliveryDate?: Moment;
    paymentType?: string;
    action?: string;
    fundingBankAccount?: UnifiedBankAccount;

    // Allow any additional properties from the actual model
    [key: string]: any;
}

export enum PayableFulfillmentType {
    FULFILLMENT_TYPE_PAYABLI_BILL_PAYMENT = 'payabli',
    FULFILLMENT_TYPE_LOB_BILL_PAYMENT = 'lob',
    FULFILLMENT_TYPE_PRINTED_CHECK = 'printed_check',
    FULFILLMENT_TYPE_MARK_AS_PAID = 'mark_as_paid',
}

export class PayableFulfillment {
    id: number;
    organizationId: number;
    payableId: number;
    fulfillmentType: string;
    fulfillmentReferenceType: string;
    fulfillmentReferenceId: number;
    memo: string;
    approvalRuleId: number;
    approvalStatus: string;
    approvalStatusAt: Moment;
    approvedApprovalsCount: number;
    totalApprovals: number;
    createdAt: Moment;
    updatedAt: Moment;
    deletedAt: Moment;
    scheduledPaymentDate: Moment;
    paymentSentAt: Moment;
    remainingApprovals: number;
    fulfillmentReference: FulfillmentReference;
    approvals: PayableApproval[] = [];
    approvalRule: PayableApprovalRule;
    paymentMethodType: string;
    action: string;
    fundingBankAccount: UnifiedBankAccount;
    total: number;
    hasBeenSent: boolean;
    status: string;
    hasError: boolean;
    errorAt: Moment;
    errorCode: number;
    errorDetails: object;
    errorMessage: string;

    constructor(fulfillment: any) {
        if (fulfillment.id) {
            this.id = fulfillment.id;
        }
        this.organizationId = fulfillment.organizationId || 0;
        this.payableId = fulfillment.payableId || 0;
        this.fulfillmentType = fulfillment.fulfillmentType || '';
        this.fulfillmentReferenceType = fulfillment.fulfillmentReferenceType || '';
        this.fulfillmentReferenceId = fulfillment.fulfillmentReferenceId || 0;
        this.memo = fulfillment.memo || '';
        this.approvalRuleId = fulfillment.approvalRuleId || 0;
        this.approvalStatus = fulfillment.approvalStatus || '';
        this.status = fulfillment.status;

        // error details
        this.hasError = fulfillment.hasError || false;
        this.errorAt = fulfillment.errorAt ? moment.utc(fulfillment.errorAt, 'YYYY-MM-DD hh:mm:ss') : null;
        this.errorCode = fulfillment.errorCode || null;
        this.errorDetails = fulfillment.errorDetails || null;
        this.errorMessage = fulfillment.errorMessage || null;

        if (this.errorMessage && this.errorMessage.toLowerCase().includes('address')) {
            this.errorMessage = this.errorMessage + '. Please verify both your organization and vendor addresses are correct.';
        }

        // approvals
        if (fulfillment.approvals && fulfillment.approvals.length > 0) {
            this.approvals = fulfillment.approvals.map((approval: any) => new PayableApproval(approval));
        }
        if (fulfillment.approvalRule) {
            this.approvalRule = new PayableApprovalRule(fulfillment.approvalRule);
        }

        this.approvedApprovalsCount = fulfillment.approvedApprovalsCount ?? 0;
        this.totalApprovals = fulfillment.totalApprovals;

        // Create appropriate fulfillment reference based on type
        if (fulfillment.fulfillmentReference) {
            this.fulfillmentReference = this.createFulfillmentReference(fulfillment.fulfillmentReference, fulfillment.fulfillmentType);
        }

        // timestamps
        const timezone = LegFiJwtService.getTimezone();
        if (fulfillment.scheduledPaymentDate) {
            this.scheduledPaymentDate = moment.utc(fulfillment.scheduledPaymentDate);
        }
        if (fulfillment.paymentSentAt) {
            this.paymentSentAt = moment.utc(fulfillment.paymentSentAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (fulfillment.approvalStatusAt) {
            this.approvalStatusAt = moment.utc(fulfillment.approvalStatusAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (fulfillment.createdAt) {
            this.createdAt = moment.utc(fulfillment.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (fulfillment.updatedAt) {
            this.updatedAt = moment.utc(fulfillment.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (fulfillment.deletedAt) {
            this.deletedAt = moment.utc(fulfillment.deletedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (fulfillment.remainingApprovals) {
            this.remainingApprovals = fulfillment.remainingApprovals;
        }
        this.paymentMethodType = fulfillment.paymentMethodType;
        this.action = fulfillment.action;
        this.fundingBankAccount = fulfillment.fundingBankAccount;
        this.total = fulfillment.total || 0;
        this.hasBeenSent = fulfillment.hasBeenSent;
    }

    // Create the appropriate fulfillment reference type based on fulfillment type
    private createFulfillmentReference(referenceData: any, fulfillmentType: string): FulfillmentReference {
        // Always preserve the original model
        switch (fulfillmentType) {
            case PayableFulfillmentType.FULFILLMENT_TYPE_PAYABLI_BILL_PAYMENT:
                const payabliPayment = new PayabliPayment(referenceData);
                return {
                    ...payabliPayment,
                    paymentAmount: payabliPayment.paymentAmount,
                    scheduledPaymentDate: payabliPayment.scheduledPaymentDate,
                    paymentSentAt: payabliPayment.sentToPayabliAt,
                    paymentType: payabliPayment.paymentType,
                    fundingBankAccount: this.fundingBankAccount, // Use fulfillment's UnifiedBankAccount
                    payabliTransactionNumber: payabliPayment.payabliTransactionNumber,
                    status: payabliPayment.payabliBillStatus
                };

            case PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT:
                const billPayment = new BillPayment(referenceData);
                return {
                    ...billPayment,
                    paymentAmount: billPayment.amount,
                    expectedDeliveryDate: billPayment.expectedDeliveryDate,
                    paymentSentAt: billPayment.sendDate,
                    fundingBankAccount: this.fundingBankAccount, // Use fulfillment's UnifiedBankAccount
                    checkNumber: billPayment.checkNumber,
                    trackingNumber: billPayment.trackingNumber,
                    status: billPayment.status
                };

            case PayableFulfillmentType.FULFILLMENT_TYPE_PRINTED_CHECK:
                const printableCheck = new PrintableCheck(referenceData);
                return {
                    ...printableCheck,
                    paymentAmount: printableCheck.amount,
                    paymentSentAt: printableCheck.checkDate,
                    fundingBankAccount: this.fundingBankAccount, // Use fulfillment's UnifiedBankAccount
                    checkNumber: printableCheck.checkNumber,
                    status: printableCheck.status
                };

            case PayableFulfillmentType.FULFILLMENT_TYPE_MARK_AS_PAID:
                const transaction = new Transaction(referenceData);
                return {
                    ...transaction,
                    id: referenceData.id,
                    paymentAmount: referenceData.amount || referenceData.paymentAmount || this.total,
                    scheduledPaymentDate: referenceData.scheduledPaymentDate ? moment(referenceData.scheduledPaymentDate) : null,
                    paymentSentAt: referenceData.paymentSentAt || referenceData.paidAt ? moment(referenceData.paymentSentAt || referenceData.paidAt) : null,
                    expectedDeliveryDate: referenceData.expectedDeliveryDate ? moment(referenceData.expectedDeliveryDate) : null,
                    paymentType: referenceData.paymentType || this.paymentMethodType || this.fulfillmentType,
                    fundingBankAccount: this.fundingBankAccount, // Use fulfillment's UnifiedBankAccount
                    status: referenceData.status,
                    checkNumber: referenceData.checkNumber || referenceData.referenceNumber,
                    action: referenceData.action || 'Marked as Paid'
                };

            default:
                // For unknown types or Transaction types, create a generic reference
                return {
                    ...referenceData,
                    id: referenceData.id,
                    paymentAmount: referenceData.amount || referenceData.paymentAmount,
                    scheduledPaymentDate: referenceData.scheduledPaymentDate ? moment(referenceData.scheduledPaymentDate) : null,
                    paymentSentAt: referenceData.paymentSentAt || referenceData.sentAt ? moment(referenceData.paymentSentAt || referenceData.sentAt) : null,
                    expectedDeliveryDate: referenceData.expectedDeliveryDate ? moment(referenceData.expectedDeliveryDate) : null,
                    paymentType: referenceData.paymentType || referenceData.type,
                    fundingBankAccount: this.fundingBankAccount, // Use fulfillment's UnifiedBankAccount
                    status: referenceData.status
                };
        }
    }


}
